import { BreadcrumbsItem } from '@atlaskit/breadcrumbs';
import { Stack } from '@atlaskit/primitives';
import { useCallback, useEffect, useState } from 'react';

import type {
	CodeReviewerRepositorySetting,
	RepositorySettingValue,
} from '../../../../../../typings/globals';
import { PageSection } from '../../../../common/components/page-section';
import { fg } from '../../../../controllers/feature-gates';
import { useRovoDevWebAnalyticsClient } from '../../../../controllers/rovo-dev-web-analytics-events';
import { settingsCodeReviewerPath } from '../../../../routes/paths';
import { RovoDevAgentsPageHeader } from '../../components/rovo-dev-agents-page-header';
import { GithubOrganisationList } from '../components/github-organisation-list';
import { GithubRepositoryList } from '../components/github-repository-list';
import { GithubRepositorySettings } from '../components/github-repository-settings';
import { ACCEPTANCE_CRITERIA_GATE_ENABLED } from '../const';

/* eslint-disable react/jsx-curly-newline */
export interface GCViewMode {
	viewKey: 'organisations' | 'repositories' | 'repository-settings';
	selectedOrg: GH.Organisation | null;
	selectedRepo: GH.Repository | null;
}
class Organisations implements GCViewMode {
	viewKey: 'organisations';

	selectedOrg: null;

	selectedRepo: null;

	constructor() {
		this.viewKey = 'organisations';
		this.selectedOrg = null;
		this.selectedRepo = null;
	}
}
class Repositories implements GCViewMode {
	viewKey: 'repositories';

	selectedOrg: GH.Organisation;

	selectedRepo: null;

	constructor(selectedOrg: GH.Organisation) {
		this.viewKey = 'repositories';
		this.selectedOrg = selectedOrg;
		this.selectedRepo = null;
	}
}
class RepositorySettings implements GCViewMode {
	viewKey: 'repository-settings';

	selectedOrg: GH.Organisation;

	selectedRepo: GH.Repository;

	constructor(selectedOrg: GH.Organisation, selectedRepo: GH.Repository) {
		this.viewKey = 'repository-settings';
		this.selectedOrg = selectedOrg;
		this.selectedRepo = selectedRepo;
	}
}

export const CodeReviewerGithubConnections = () => {
	const [viewMode, setViewMode] = useState<GCViewMode>(new Organisations());
	const { fireScreenEvent } = useRovoDevWebAnalyticsClient();

	const setSelectedOrg = useCallback(
		(org: GH.Organisation) => {
			setViewMode(new Repositories(org));
		},
		[setViewMode],
	);

	const clearSelectedOrg = useCallback(() => setViewMode(new Organisations()), [setViewMode]);

	const clearSelectedRepo = useCallback(
		(org: GH.Organisation) => setViewMode(new Repositories(org)),
		[setViewMode],
	);

	const setSelectedRepo = useCallback(
		(newRepo: GH.Repository, selectedOrg: GH.Organisation) => {
			setViewMode(new RepositorySettings(selectedOrg, newRepo));
		},
		[setViewMode],
	);

	const updateSelectedRepoSetting = useCallback(
		(update: RepositorySettingValue, selectedRepo: GH.Repository, selectedOrg: GH.Organisation) => {
			const newRepo: GH.Repository = {
				...selectedRepo,
				autoreviewSettings: {
					...selectedRepo.autoreviewSettings,
					value: update,
				} as CodeReviewerRepositorySetting,
			};

			setSelectedRepo(newRepo, selectedOrg);
		},
		[setSelectedRepo],
	);

	useEffect(() => {
		fireScreenEvent({
			name:
				viewMode.viewKey === 'organisations'
					? 'view-github-organisations'
					: 'view-github-repositories',
		});
	}, [fireScreenEvent, viewMode.viewKey]);

	if (viewMode.viewKey === 'organisations') {
		return (
			<PageSection>
				<Stack space="space.200">
					<RovoDevAgentsPageHeader
						additionalBreadcrumbItems={[
							<BreadcrumbsItem
								href={`/dev-agents${settingsCodeReviewerPath}`}
								text="Set up Code Reviewer for GitHub"
								key="set-up-code-reviewer-for-github"
							/>,
						]}
						headingText="Set up Code Reviewer for GitHub"
						descriptionText="
						Select the GitHub organizations and repositories you want to enable the
					  Code Reviewer on. When the Code Reviewer is enabled, it automatically review new pull
						requests and leave feedbacks in pull request comments. You must be a Rovo Dev Agents
						admin to enable Code Reviewer on a GitHub organization.
						"
					/>
					<GithubOrganisationList setSelectedOrg={setSelectedOrg} />
				</Stack>
			</PageSection>
		);
	}

	if (viewMode.viewKey === 'repositories' && viewMode.selectedOrg) {
		return (
			<PageSection>
				<Stack space="space.300">
					<RovoDevAgentsPageHeader
						additionalBreadcrumbItems={[
							<BreadcrumbsItem
								onClick={() => clearSelectedOrg()}
								href={`/dev-agents${settingsCodeReviewerPath}`}
								text="Set up Code Reviewer for GitHub"
								key="set-up-code-reviewer-for-github"
							/>,
							<BreadcrumbsItem
								text={viewMode.selectedOrg.owner}
								key={viewMode.selectedOrg.owner}
							/>,
						]}
						headingText={`${viewMode.selectedOrg.owner}`}
						descriptionText="
						Select the repositories in your organization you want to enable Code Reviewer on. When
						Code Reviewer is enabled on a repository, it automatically reviews new pull requests and
						leaves feedback in pull request comments. You must be a Rovo Dev Agents admin to enable
						Code Reviewer on a repository.
						"
					/>
					<GithubRepositoryList
						selectedOrg={viewMode.selectedOrg}
						setSelectedRepo={(newRepo) =>
							setSelectedRepo(newRepo, viewMode.selectedOrg as GH.Organisation)
						} // this cast is safe because we know selectedOrg is not null here
					/>
				</Stack>
			</PageSection>
		);
	}

	if (
		viewMode.viewKey === 'repository-settings' &&
		viewMode.selectedOrg &&
		viewMode.selectedOrg.autoreviewSettings &&
		viewMode.selectedRepo &&
		fg(ACCEPTANCE_CRITERIA_GATE_ENABLED)
	) {
		return (
			<PageSection>
				<Stack space="space.300">
					<RovoDevAgentsPageHeader
						additionalBreadcrumbItems={[
							<BreadcrumbsItem
								onClick={() => clearSelectedOrg()}
								href={`/dev-agents${settingsCodeReviewerPath}`}
								text="Set up Code Reviewer for GitHub"
								key="set-up-code-reviewer-for-github"
							/>,
							<BreadcrumbsItem
								onClick={() => clearSelectedRepo(viewMode.selectedOrg as GH.Organisation)}
								text={viewMode.selectedOrg.owner}
								key={viewMode.selectedOrg.owner}
							/>,
							<BreadcrumbsItem
								text={`Manage settings for ${viewMode.selectedRepo.repoName}`}
								key={viewMode.selectedRepo.repoName}
							/>,
						]}
						headingText={`Manage settings for ${viewMode.selectedRepo.repoName}`}
						descriptionText="
						Code Reviewer is an AI Agent that helps your developers improve their pull requests.
						It automatically comments on pull requests and gives suggestions on topics like code
						readability, performance, typos, bugs, and more.
						"
					/>
					<GithubRepositorySettings
						selectedRepo={viewMode.selectedRepo}
						updateRepoSettings={(update: RepositorySettingValue) =>
							updateSelectedRepoSetting(
								update,
								viewMode.selectedRepo as GH.Repository,
								viewMode.selectedOrg as GH.Organisation,
							)
						}
					/>
				</Stack>
			</PageSection>
		);
	}

	// TODO: Replace with empty state
	return <PageSection>Error</PageSection>;
};
